import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: email.trim().toLowerCase(), password })
      });

      if (response.ok) {
        navigate('/admin/dashboard');
        return;
      }

      if (response.status === 401) {
        setError('Invalid email or password.');
      } else if (response.status === 429) {
        setError('Too many attempts. Please wait a moment and try again.');
      } else {
        // 5xx / unexpected - surface the actual reason so we don't hide a real outage.
        const body = await response.json().catch(() => ({}));
        const detail = typeof body.detail === 'string' ? body.detail : null;
        setError(detail || `Sign-in failed (HTTP ${response.status}). Please try again.`);
      }
    } catch (err) {
      // TypeError: Failed to fetch - CORS, network, or the cross-origin
      // backend rewrite isn't in effect yet. Make the error actionable.
      console.error('[AdminLogin] fetch failed:', err);
      setError(
        'Could not reach the server. Check your connection and try again. ' +
        '(If this keeps happening, contact support - error: ' + (err?.message || 'network') + ')'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F6EF] flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="bg-[#FFFFFF] border border-[#F4ECD8] p-8">
          <div className="text-center mb-8">
            <img
              src="https://res.cloudinary.com/des9fk8et/image/upload/e_trim,f_auto,q_auto:good/mundalo/brand/mundalo-logo-green.png"
              alt="Mundalo"
              className="block h-10 w-[200px] object-contain mx-auto mb-5"
            />
            <h1 className="text-2xl font-bold text-[#0A1F2E]">Admin Access</h1>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            {error && (
              <div className="bg-red-900/20 border border-red-800 text-red-400 px-4 py-3 text-sm" data-testid="admin-login-error">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-2 text-[#0A1F2E]">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-[#E0D6B8] bg-[#F9F6EF] text-[#0A1F2E]"
                placeholder="admin@mundalo.com"
                required
                autoFocus
                autoComplete="email"
                data-testid="admin-email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-[#0A1F2E]">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-[#E0D6B8] bg-[#F9F6EF] text-[#0A1F2E]"
                placeholder="Enter admin password"
                required
                autoComplete="current-password"
                data-testid="admin-password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#2CA8A0] text-[#F9F6EF] px-6 py-3 font-semibold hover:bg-[#2CA8A0] transition disabled:opacity-50"
              data-testid="admin-login-btn"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <a href="/" className="text-sm text-[#5A6B73] hover:text-[#2CA8A0] transition">
              ← Back to website
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
